import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { academyAdminAPI } from '../../lib/academyApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Save, CreditCard, Globe, Mail, Eye, EyeOff, FileText } from 'lucide-react';
import ImagePicker from '../../components/cms/ImagePicker';

const AcademySettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try { const res = await academyAdminAPI.getSettings(); setSettings(res.data); }
    catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await academyAdminAPI.updateSettings(settings); toast.success('Parametres sauvegardes'); }
    catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const updateStripe = (field, value) => {
    setSettings({...settings, stripe_settings: {...(settings.stripe_settings || {}), [field]: value}});
  };

  if (loading || !settings) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="academy-settings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">Parametres aCADemie</h1>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Sauvegarder
        </Button>
      </div>

      <div className="space-y-6">
        {/* General */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#F9B438]" />General</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Titre d'introduction</Label><Input value={settings.intro_title || ''} onChange={e => setSettings({...settings, intro_title: e.target.value})} /></div>
            <div><Label>Sous-titre hero</Label><Input value={settings.hero_subtitle || ''} onChange={e => setSettings({...settings, hero_subtitle: e.target.value})} /></div>
            <div><Label>Texte d'introduction</Label><Textarea value={settings.intro_text || ''} onChange={e => setSettings({...settings, intro_text: e.target.value})} rows={6} /></div>
            <ImagePicker value={settings.hero_image_url || ''} onChange={url => setSettings({...settings, hero_image_url: url})} folder="formations" label="Image hero" />
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Texte CTA</Label><Input value={settings.cta_text || ''} onChange={e => setSettings({...settings, cta_text: e.target.value})} /></div>
              <div><Label>URL CTA</Label><Input value={settings.cta_url || ''} onChange={e => setSettings({...settings, cta_url: e.target.value})} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Enterprise section */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#F9B438]" />Section entreprise</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Titre</Label><Input value={settings.enterprise_section_title || ''} onChange={e => setSettings({...settings, enterprise_section_title: e.target.value})} /></div>
            <div><Label>Texte</Label><Textarea value={settings.enterprise_section_text || ''} onChange={e => setSettings({...settings, enterprise_section_text: e.target.value})} rows={4} /></div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#F9B438]" />Notifications</CardTitle></CardHeader>
          <CardContent>
            <div><Label>Courriel de notification</Label><Input value={settings.notification_email || ''} onChange={e => setSettings({...settings, notification_email: e.target.value})} placeholder="academie@peyconsultants.ca" /></div>
          </CardContent>
        </Card>

        {/* Stripe & Payment */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#F9B438]" />Paiements</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-sm font-medium text-green-800">Stripe est la seule methode de paiement active</p>
              <p className="text-xs text-green-600 mt-0.5">Tous les paiements sont traites de facon securisee via Stripe</p>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium text-sm mb-3">Configuration Stripe</h4>
              <div className="space-y-3">
                <div>
                  <Label>Mode</Label>
                  <select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={settings.stripe_settings?.mode || 'test'} onChange={e => updateStripe('mode', e.target.value)}>
                    <option value="test">Test</option>
                    <option value="live">Production</option>
                  </select>
                </div>
                <div>
                  <Label>Cle publique (Publishable Key)</Label>
                  <Input value={settings.stripe_settings?.publishable_key || ''} onChange={e => updateStripe('publishable_key', e.target.value)} placeholder="pk_test_..." />
                </div>
                <div>
                  <Label>Cle secrete (Secret Key)</Label>
                  <div className="relative">
                    <Input type={showStripeKey ? 'text' : 'password'} value={settings.stripe_settings?.secret_key || ''} onChange={e => updateStripe('secret_key', e.target.value)} placeholder="sk_test_..." />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600" onClick={() => setShowStripeKey(!showStripeKey)}>
                      {showStripeKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">La cle secrete est utilisee cote serveur pour creer les sessions de paiement</p>
                </div>
                <div>
                  <Label>Secret Webhook</Label>
                  <Input type="password" value={settings.stripe_settings?.webhook_secret || ''} onChange={e => updateStripe('webhook_secret', e.target.value)} placeholder="whsec_..." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcademySettings;
